// Enhanced Dashboard Component — REAL DATA, zero Math.random() mocks
// Każdy widget podłączony do prawdziwego API backendu :8888

import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import VoiceCommandPanel from './VoiceCommandPanel';
import MCPToolsModalPanel from './MCPToolsModalPanel';
import FileAnalysisPanel from './FileAnalysisPanel';
import ProcessManagementPanel from './ProcessManagementPanel';

interface EnhancedDashboardProps {
  stats: any;
  setStats: React.Dispatch<React.SetStateAction<any>>;
  addLog?: (level: 'INFO' | 'WARN' | 'ERROR' | 'AI_ACTION', message: string, component?: string) => void;
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe'];

// API base: empty = same origin (works for both localhost and Telebit tunnel)
const API_BASE = '';

// Dynamic API key reader — always fresh from localStorage
const getApiKey = (): string => {
  const invalid = new Set(['admin-initial-key', 'admin-init-key', 'nexus-admin-2026', '']);
  const k = localStorage.getItem('omnigate_api_key') || '';
  return invalid.has(k) ? '' : k;
};

async function apiFetch(path: string, options?: RequestInit) {
  const key = getApiKey();
  const authHeaders: Record<string, string> = key
    ? { 'Authorization': `Bearer ${key}`, 'X-API-Key': key }
    : {};
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options?.headers as Record<string, string> || {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const EnhancedDashboard: React.FC<EnhancedDashboardProps> = ({ stats, setStats, addLog }) => {
  const [activePanel, setActivePanel] = useState<'voice' | 'mcp' | 'file' | 'process' | null>(null);

  // --- Real metrics state ---
  const [systemHealth, setSystemHealth] = useState<number>(0);
  const [systemHealthLabel, setSystemHealthLabel] = useState<string>('---');
  const [mcpToolsCount, setMcpToolsCount] = useState<number>(0);
  const [sessionCount, setSessionCount] = useState<number>(0);
  const [errorCount, setErrorCount] = useState<number>(0);

  // Watchdog breakdown
  const [watchdogData, setWatchdogData] = useState<any>(null);

  // System load history — CPU/RAM/VRAM from watchdog (REAL)
  const [systemLoad, setSystemLoad] = useState<any[]>([]);

  // Recent activities from chat history
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  // Function usage pie chart (MCP categories if available, else service status)
  const [functionUsage, setFunctionUsage] = useState<any[]>([]);

  // Services status list
  const [services, setServices] = useState<{ name: string; status: string; details?: string }[]>([]);

  // Fetch watchdog status → systemHealth, CPU, RAM, VRAM, services
  const fetchWatchdog = useCallback(async () => {
    try {
      const data = await apiFetch('/v1/watchdog/status');
      setWatchdogData(data);

      // Health score: OK→100, WARNING→75, CRITICAL→40
      const healthMap: Record<string, number> = { OK: 100, WARNING: 75, CRITICAL: 40 };
      setSystemHealth(healthMap[data.overall] ?? 0);
      setSystemHealthLabel(data.overall ?? '---');

      // Services list
      if (data.services) {
        const svcList = Object.entries(data.services).map(([name, info]: [string, any]) => ({
          name,
          status: info.status,
          details: info.details
        }));
        setServices(svcList);

        // Function usage pie = services by status
        const ok = svcList.filter(s => s.status === 'ok').length;
        const warn = svcList.filter(s => s.status === 'warning').length;
        const down = svcList.filter(s => s.status === 'down' || s.status === 'error').length;
        setFunctionUsage([
          { name: 'OK', value: ok || 1 },
          { name: 'Warning', value: warn },
          { name: 'Down', value: down }
        ].filter(x => x.value > 0));
      }

      // Append to load history
      const ts = new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setSystemLoad(prev => [
        ...prev.slice(-29),
        {
          time: ts,
          cpu: Math.round(data.cpu?.percent ?? 0),
          ram: Math.round(data.ram?.percent ?? 0),
          vram: Math.round(data.vram?.percent ?? 0)
        }
      ]);
    } catch {
      // Backend offline – push zeros so graph keeps moving
      const ts = new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setSystemLoad(prev => [...prev.slice(-29), { time: ts, cpu: 0, ram: 0, vram: 0 }]);
      setSystemHealthLabel('OFFLINE');
    }
  }, []);

  // Fetch MCP tools count
  const fetchMcpTools = useCallback(async () => {
    try {
      const data = await apiFetch('/v1/mcp/tools');
      // endpoint returns { tools: [...] } or array
      const list = Array.isArray(data) ? data : (data.tools ?? []);
      setMcpToolsCount(list.length);
    } catch {
      setMcpToolsCount(0);
    }
  }, []);

  // Fetch chat history → session count + recent activities
  const fetchHistory = useCallback(async () => {
    try {
      const data = await apiFetch('/v1/history/chat?limit=10');
      // data is array of messages or { history: [...] }
      const messages: any[] = Array.isArray(data) ? data : (data.history ?? data.messages ?? []);

      // Count unique session_ids
      const sessions = new Set(messages.map((m: any) => m.session_id || m.id));
      setSessionCount(sessions.size || messages.length);

      // Build recent activities from real messages
      const activities = messages.slice(0, 6).map((m: any) => {
        const role = m.role ?? m.type ?? 'user';
        const content = (m.content ?? m.message ?? '').toString().slice(0, 60);
        const ts = m.timestamp ?? m.created_at ?? '';
        const timeStr = ts ? new Date(ts).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : 'just now';
        return {
          time: timeStr,
          action: content || `${role} message`,
          type: role === 'assistant' ? 'ai' : 'user',
          status: 'success'
        };
      });
      setRecentActivities(activities);
      setErrorCount(messages.filter((m: any) => m.error || m.status === 'error').length);
    } catch {
      // No history yet – show empty
      setRecentActivities([]);
    }
  }, []);

  // Fetch models list (to show model count somewhere)
  const fetchModels = useCallback(async () => {
    try {
      const data = await apiFetch('/v1/models');
      const modelList = data.data ?? data.models ?? [];
      // setModelsCount(modelList.length);  // could expose in UI later
    } catch { /* skip */ }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchWatchdog();
    fetchMcpTools();
    fetchHistory();
    fetchModels();

    // Refresh intervals
    const watchdogInterval = setInterval(fetchWatchdog, 5000);
    const mcpInterval = setInterval(fetchMcpTools, 30000);
    const historyInterval = setInterval(fetchHistory, 15000);

    return () => {
      clearInterval(watchdogInterval);
      clearInterval(mcpInterval);
      clearInterval(historyInterval);
    };
  }, [fetchWatchdog, fetchMcpTools, fetchHistory, fetchModels]);

  // Also sync CPU/GPU/RAM from stats (WebSocket data from App.tsx)
  useEffect(() => {
    if (stats?.cpuLoad !== undefined) {
      const ts = new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setSystemLoad(prev => {
        const last = prev[prev.length - 1];
        // Only add if stats actually changed to avoid duplicate points
        if (last && last.cpu === Math.round(stats.cpuLoad)) return prev;
        return [
          ...prev.slice(-29),
          {
            time: ts,
            cpu: Math.round(stats.cpuLoad ?? 0),
            ram: Math.round(stats.ramUsed && stats.ramTotal ? (stats.ramUsed / stats.ramTotal) * 100 : 0),
            vram: Math.round(stats.vramUsed && stats.vramTotal ? (stats.vramUsed / stats.vramTotal) * 100 : 0)
          }
        ];
      });
    }
  }, [stats]);

  const healthColor = systemHealth >= 90 ? 'green' : systemHealth >= 60 ? 'yellow' : 'red';
  const healthColorMap: Record<string, string> = {
    green: 'from-green-900/30 to-green-800/20 border-green-800/30 text-green-400',
    yellow: 'from-yellow-900/30 to-yellow-800/20 border-yellow-800/30 text-yellow-400',
    red: 'from-red-900/30 to-red-800/20 border-red-800/30 text-red-400'
  };

  const renderActivityIcon = (type: string) => {
    const map: Record<string, string> = {
      ai: 'fa-robot text-purple-400',
      user: 'fa-user text-blue-400',
      voice: 'fa-microphone text-green-400',
      error: 'fa-exclamation-triangle text-red-400'
    };
    return <i className={`fas ${map[type] ?? 'fa-comment text-slate-400'}`} />;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header Stats — REAL DATA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* System Health */}
        <div className={`bg-gradient-to-br ${healthColorMap[healthColor]} p-4 rounded-xl border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-bold uppercase tracking-wider ${healthColorMap[healthColor].split(' ').find(c => c.startsWith('text-'))}`}>
                System Health
              </p>
              <p className="text-2xl font-black text-white mt-1">{systemHealthLabel}</p>
              {watchdogData && (
                <p className="text-xs text-slate-400 mt-0.5">
                  CPU {Math.round(watchdogData.cpu?.percent ?? 0)}%&nbsp;·&nbsp;
                  RAM {Math.round(watchdogData.ram?.percent ?? 0)}%&nbsp;·&nbsp;
                  VRAM {Math.round(watchdogData.vram?.percent ?? 0)}%
                </p>
              )}
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <i className="fas fa-heartbeat text-green-400 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Active Sessions */}
        <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 p-4 rounded-xl border border-blue-800/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-blue-400 uppercase tracking-wider">Active Sessions</p>
              <p className="text-2xl font-black text-white mt-1">{sessionCount}</p>
              <p className="text-xs text-slate-400 mt-0.5">z historii chatu</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <i className="fas fa-users text-blue-400 text-xl"></i>
            </div>
          </div>
        </div>

        {/* MCP Tools */}
        <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 p-4 rounded-xl border border-purple-800/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-purple-400 uppercase tracking-wider">MCP Tools</p>
              <p className="text-2xl font-black text-white mt-1">{mcpToolsCount || '…'}</p>
              <p className="text-xs text-slate-400 mt-0.5">z /v1/mcp/tools</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
              <i className="fas fa-toolbox text-purple-400 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Errors */}
        <div className="bg-gradient-to-br from-red-900/30 to-red-800/20 p-4 rounded-xl border border-red-800/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-red-400 uppercase tracking-wider">Errors (historia)</p>
              <p className="text-2xl font-black text-white mt-1">{errorCount}</p>
              <p className="text-xs text-slate-400 mt-0.5">z chat history</p>
            </div>
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
              <i className="fas fa-exclamation-triangle text-red-400 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Load Chart — REAL: CPU / RAM / VRAM from watchdog */}
        <div className="bg-[#1e222d] p-5 rounded-xl border border-[#2a2e39]">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-2">
            <i className="fas fa-chart-line text-purple-500"></i>
            System Performance (REAL)
          </h3>
          <p className="text-[10px] text-slate-500 mb-3">CPU • RAM • VRAM — odświeżane co 5s z /v1/watchdog/status</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <AreaChart data={systemLoad}>
                <defs>
                  <linearGradient id="gCpu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gRam" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gVram" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2e39" vertical={false} />
                <XAxis dataKey="time" hide />
                <YAxis domain={[0, 100]} stroke="#4c525e" fontSize={11} tickFormatter={v => `${v}%`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#131722', borderColor: '#2a2e39', borderRadius: '6px', fontSize: '11px' }}
                  formatter={(v: any, name: string) => [`${v}%`, name.toUpperCase()]}
                />
                <Area type="monotone" dataKey="cpu" name="cpu" stroke="#f97316" fillOpacity={1} fill="url(#gCpu)" dot={false} />
                <Area type="monotone" dataKey="ram" name="ram" stroke="#3b82f6" fillOpacity={1} fill="url(#gRam)" dot={false} />
                <Area type="monotone" dataKey="vram" name="vram" stroke="#8b5cf6" fillOpacity={1} fill="url(#gVram)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-2 text-[10px]">
            <span className="text-orange-400">● CPU</span>
            <span className="text-blue-400">● RAM</span>
            <span className="text-purple-400">● VRAM</span>
          </div>
        </div>

        {/* Services Status Pie */}
        <div className="bg-[#1e222d] p-5 rounded-xl border border-[#2a2e39]">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-2">
            <i className="fas fa-chart-pie text-blue-500"></i>
            Services Status (REAL)
          </h3>
          <p className="text-[10px] text-slate-500 mb-3">Z /v1/watchdog/status → services</p>

          {services.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-500 text-sm">
              Ładowanie danych serwisów…
            </div>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <PieChart>
                    <Pie data={functionUsage} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                      {functionUsage.map((_, i) => (
                        <Cell key={i} fill={[COLORS[2], COLORS[3], COLORS[4]][i] ?? COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any, n: string) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
                {services.map((svc, i) => (
                  <div key={i} className="flex items-center justify-between text-xs px-2 py-1 bg-[#131722]/50 rounded">
                    <span className="text-slate-300 font-mono">{svc.name}</span>
                    <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${svc.status === 'ok' ? 'bg-green-500/20 text-green-400' :
                        svc.status === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                      }`}>{svc.status.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Recent Activities — REAL from /v1/history */}
      <div className="bg-[#1e222d] p-5 rounded-xl border border-[#2a2e39]">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-2">
          <i className="fas fa-history text-yellow-500"></i>
          Recent Activities (REAL)
        </h3>
        <p className="text-[10px] text-slate-500 mb-3">Z /v1/history — ostatnie wiadomości z historii chatu</p>
        {recentActivities.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-4">Brak historii chatu lub backend offline</p>
        ) : (
          <div className="space-y-2">
            {recentActivities.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#131722]/50 rounded-lg">
                <div className="text-lg w-6 text-center">{renderActivityIcon(a.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{a.action}</p>
                  <p className="text-xs text-slate-400">{a.time}</p>
                </div>
                <div className="px-2 py-1 rounded text-[10px] font-bold bg-green-500/20 text-green-400">
                  {a.status.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { key: 'voice', icon: 'fa-microphone', color: 'blue', label: 'Voice Command', sub: 'Mów by sterować' },
          { key: 'mcp', icon: 'fa-tools', color: 'purple', label: 'MCP Tools', sub: 'Uruchom narzędzia' },
          { key: 'file', icon: 'fa-file-alt', color: 'green', label: 'File Analysis', sub: 'Analizuj pliki' },
          { key: 'process', icon: 'fa-cogs', color: 'yellow', label: 'Process Mgmt', sub: 'Zarządzaj procesami' }
        ].map(({ key, icon, color, label, sub }) => (
          <button
            key={key}
            onClick={() => setActivePanel(key as any)}
            className="bg-[#1e222d] hover:bg-[#2a2e39] p-4 rounded-xl border border-[#2a2e39] transition-all group cursor-pointer"
          >
            <div className="text-center">
              <div className={`w-12 h-12 bg-${color}-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-${color}-500/30 transition-colors`}>
                <i className={`fas ${icon} text-${color}-400 text-xl`}></i>
              </div>
              <p className="text-white font-bold text-sm">{label}</p>
              <p className="text-xs text-slate-400 mt-1">{sub}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Modal Panels */}
      {activePanel === 'voice' && addLog && (
        <VoiceCommandPanel onClose={() => setActivePanel(null)} addLog={addLog} />
      )}
      {activePanel === 'mcp' && addLog && (
        <MCPToolsModalPanel onClose={() => setActivePanel(null)} addLog={addLog} />
      )}
      {activePanel === 'file' && addLog && (
        <FileAnalysisPanel onClose={() => setActivePanel(null)} addLog={addLog} />
      )}
      {activePanel === 'process' && addLog && (
        <ProcessManagementPanel onClose={() => setActivePanel(null)} addLog={addLog} />
      )}
    </div>
  );
};

export default EnhancedDashboard;
